
/// <amd-module name="Release/Commands/ReleaseCommandsService" />
import * as React from "react";

import { IVssService, VssService, Services, IVssPageContext } from "VSS/Platform/Context";
import { IVssLayoutManager, IWrappedComponentProperties, WrappedComponent } from "VSS/Platform/Layout";
import { localeFormat } from "VSS/Core/Util/String";

import { IIconProps } from "VSSUI/Icon";
import { IMenuItem } from "VSSUI/Menu";

import { IReleaseMenuCommandContext } from "Release/Client/ExtensionContracts/IMenuCommandContracts";
import { IReleaseService } from "Release/Client/Service/Release";
import { Release } from "Release/Client/Contracts/Release";
import { hasRmPermissions, ReleaseManagementSecurityPermissions } from "Release/Helpers/Permission";
import { publishClickEvent, publishFailedEvent, publishSuccessEvent } from "Release/Helpers/Telemetry";
import { getEnvironmentNamesInProgress } from "Release/Helpers/ReleaseEnvironment";
import { createUrlForRoute, handleEventAndNavigate } from "Release/Helpers/Link";
import { IPipelineDialogWithCommentProps } from "./DialogTypes";
import * as Resources from "Release/Commands/Resources";

export const viewReleaseCommand: (context: IReleaseMenuCommandContext) => IMenuItem | undefined = (context: IReleaseMenuCommandContext) => {
    const definition = context.release.releaseDefinition;
    if (hasRmPermissions(context.pageContext, definition.path, definition.id, ReleaseManagementSecurityPermissions.ViewReleases)) {
        return _getContextMenuItem(context, "view-release-command", Resources.ViewRelease, { iconName: "Forward" }, "viewRelease", _getReleaseUrl(context.pageContext, context));
    }
};

export const abandonReleaseCommand: (context: IReleaseMenuCommandContext) => IMenuItem | undefined = (context: IReleaseMenuCommandContext) => {
    const definition = context.release.releaseDefinition;
    if (hasRmPermissions(context.pageContext, definition.path, definition.id, ReleaseManagementSecurityPermissions.ManageReleases)) {
        return _getContextMenuItem(context, "abandon-release-command", Resources.AbandonReleaseMenuItemText, { iconName: "Blocked" }, "abandonRelease");
    }
};

export const deleteReleaseCommand: (context: IReleaseMenuCommandContext) => IMenuItem | undefined = (context: IReleaseMenuCommandContext) => {
    const definition = context.release.releaseDefinition;
    if (hasRmPermissions(context.pageContext, definition.path, definition.id, ReleaseManagementSecurityPermissions.DeleteReleases)) {
        return _getContextMenuItem(context, "delete-release-command", Resources.DeleteReleaseMenuItemText, { iconName: "Delete" }, "deleteRelease");
    }
};

export const undeleteReleaseCommand: (context: IReleaseMenuCommandContext) => IMenuItem | undefined = (context: IReleaseMenuCommandContext) => {
    const definition = context.release.releaseDefinition;
    if (hasRmPermissions(context.pageContext, definition.path, definition.id, ReleaseManagementSecurityPermissions.ManageReleases)) {
        return _getContextMenuItem(context, "undelete-release-command", Resources.UndeleteReleaseMenuItemText, undefined, "undeleteRelease");
    }
};

export const changeRetentionCommand: (context: IReleaseMenuCommandContext) => IMenuItem | undefined = (context: IReleaseMenuCommandContext) => {
    const definition = context.release.releaseDefinition;
    if (hasRmPermissions(context.pageContext, definition.path, definition.id, ReleaseManagementSecurityPermissions.ManageReleases)) {
        const isRetained = context.release.keepForever;
        let retainReleaseItemName = isRetained ? Resources.StopRetainingReleaseMenuItemText : Resources.RetainReleaseMenuItemText;
        return _getContextMenuItem(context, "retain-release-command", retainReleaseItemName, { iconName: isRetained ? "Unlock" : "Lock" }, "changeRetention");
    }
};

export interface IReleaseCommandsService extends IVssService {
    viewRelease(context: IReleaseMenuCommandContext): void;
    abandonRelease(context: IReleaseMenuCommandContext): void;
    deleteRelease(context: IReleaseMenuCommandContext): void;
    undeleteRelease(context: IReleaseMenuCommandContext): void;
    changeRetention(context: IReleaseMenuCommandContext): void;
}

class ReleaseCommandsService extends VssService implements IReleaseCommandsService {
    public viewRelease(context: IReleaseMenuCommandContext, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void {
        publishClickEvent(this.pageContext, viewReleaseFeature, context.telemetrySource);
        const releaseUrl = _getReleaseUrl(this.pageContext, context);
        handleEventAndNavigate(this.pageContext, event, releaseUrl);
    }

    public abandonRelease(context: IReleaseMenuCommandContext): void {
        publishClickEvent(this.pageContext, abandonReleaseFeature, context.telemetrySource);
        if (context.release.id) {
            const pendingEnvironments = getEnvironmentNamesInProgress(context.release);
            let environmentsInProgress: string = "";
            if (pendingEnvironments && pendingEnvironments.length > 0) {
                environmentsInProgress = pendingEnvironments.join(",");
            }

            const okDisabled = !!environmentsInProgress;
            const footerInfoMessage = !!environmentsInProgress ? localeFormat(Resources.AbandonDialogEnvironmentsInProgress, environmentsInProgress) : "";

            _renderWrappedComponent(this.pageContext, localeFormat(Resources.AbandonReleaseDialogTitleText, context.release.name),
                (comment: string) => {
                    if (context.release.id) {
                        return this.pageContext.getService<IReleaseService>("IReleaseService")
                            .abandonRelease(context.release, comment)
                            .then((release: Release) => {
                                publishSuccessEvent(this.pageContext, abandonReleaseFeature, context.telemetrySource);
                                context.successCallback(localeFormat(Resources.AbandonReleaseConfirmationMessageFormat, context.release.name), true, release);
                            }, (error) => {
                                publishFailedEvent(this.pageContext, abandonReleaseFeature, context.telemetrySource);
                                if (context.errorCallback) {
                                    let errorMessage: string = error ? error.message || error : "";
                                    context.errorCallback(errorMessage);
                                }
                            });
                    }
                },
                [_getDialogContent(context.release.name || "", "abandon", Resources.AbandonReleaseDialogContentText)],
                okDisabled,
                footerInfoMessage);
        }
    }

    public deleteRelease(context: IReleaseMenuCommandContext): void {
        publishClickEvent(this.pageContext, deleteReleaseFeature, context.telemetrySource);
        if (context.release.id) {
            const pendingEnvironments = getEnvironmentNamesInProgress(context.release);
            let environmentsInProgress: string = "";
            if (pendingEnvironments && pendingEnvironments.length > 0) {
                environmentsInProgress = pendingEnvironments.join(",");
            }

            const okDisabled = !!environmentsInProgress;
            const footerInfoMessage = !!environmentsInProgress ? localeFormat(Resources.DeleteReleaseDialogEnvironmentsInProgress, environmentsInProgress) : "";

            _renderWrappedComponent(this.pageContext, localeFormat(Resources.DeleteReleaseDialogTitleText, context.release.name),
                (comment: string) => {
                    if (context.release.id) {
                        return this.pageContext.getService<IReleaseService>("IReleaseService")
                            .deleteRelease(context.release.id, comment)
                            .then(() => {
                                publishSuccessEvent(this.pageContext, deleteReleaseFeature, context.telemetrySource);
                                context.successCallback(localeFormat(Resources.DeleteReleaseConfirmationMessageFormat, context.release.name), true);
                            }, (error) => {
                                publishFailedEvent(this.pageContext, deleteReleaseFeature, context.telemetrySource);
                                if (context.errorCallback) {
                                    let errorMessage: string = error ? error.message || error : "";
                                    context.errorCallback(errorMessage);
                                }
                            });
                    }
                },
                [_getDialogContent(context.release.name || "", "delete", Resources.DeleteReleaseDialogContentText)],
                okDisabled,
                footerInfoMessage);
        }
    }

    public undeleteRelease(context: IReleaseMenuCommandContext): void {
        publishClickEvent(this.pageContext, undeleteReleaseFeature, context.telemetrySource);
        if (context.release.id) {
            _renderWrappedComponent(this.pageContext, Resources.UndeleteReleaseDialogTitleText,
                (comment: string) => {
                    if (context.release.id) {
                        return this.pageContext.getService<IReleaseService>("IReleaseService")
                            .undeleteRelease(context.release.id, comment)
                            .then(() => {
                                publishSuccessEvent(this.pageContext, undeleteReleaseFeature, context.telemetrySource);
                                context.successCallback(localeFormat(Resources.UndeleteReleaseConfirmationMessageFormat, context.release.name), true);
                            }, (error) => {
                                publishFailedEvent(this.pageContext, undeleteReleaseFeature, context.telemetrySource);
                                if (context.errorCallback) {
                                    let errorMessage: string = error ? error.message || error : "";
                                    context.errorCallback(errorMessage);
                                }
                            });
                    }
                },
                [_getDialogContent(context.release.name || "", "undelete", Resources.UndeleteReleaseDialogContentText)]);
        }
    }

    public changeRetention(context: IReleaseMenuCommandContext): void {
        const keepForever = !context.release.keepForever;
        publishClickEvent(this.pageContext, changeReleaseRetentionFeature, context.telemetrySource);
        this.pageContext.getService<IReleaseService>("IReleaseService").retainRelease(context.release.id, keepForever, "")
            .then((release) => {
                publishSuccessEvent(this.pageContext, changeReleaseRetentionFeature, context.telemetrySource);

                const format = keepForever ? Resources.ReleaseRetainedMessageFormat : Resources.ReleaseNotRetainedMessageFormat;
                const message = localeFormat(format, release.name);
                if (context.successCallback) {
                    context.successCallback(message, false, release);
                }
            },
                (error) => {
                    publishFailedEvent(this.pageContext, changeReleaseRetentionFeature, context.telemetrySource);

                    if (context.errorCallback) {
                        let errorMessage: string = error ? error.message || error : "";
                        context.errorCallback(errorMessage);
                    }
                });
    }
}

function _getDialogContent(releaseName: string, classNamePrefix: string, contentTextFormat: string): React.ReactNode {
    return React.createElement(
        "div",
        { className: classNamePrefix + "-release-dialog-content" },
        React.createElement("div", { style: { marginBottom: 15 } }, localeFormat(contentTextFormat, releaseName)));
}

function _renderWrappedComponent(pageContext: IVssPageContext, dialogTitle: string, onOkClick: (comment: string) => void, children?: React.ReactNode[], okDisabled?: boolean, footerInfoMessage?: string): void {

    const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");

    layoutManager.renderCallout((onDismiss: () => void) => {
        const extendedProps: IWrappedComponentProperties<IPipelineDialogWithCommentProps<void>> & IPipelineDialogWithCommentProps<void> = {
            commentBoxTitle: Resources.Comment,
            dialogTitle: dialogTitle,
            onClose: onDismiss,
            onOkClick: onOkClick,
            okDisabled: okDisabled,
            footerInfoMessage: footerInfoMessage,
            dependencies: ["ms.vss-releaseManagement-web.release-command-dialogs"],
            wrappedType: "rel-pipe-comment-dialog",
        };
        return React.createElement(WrappedComponent, extendedProps, ...(children || []));
    });
}

function _getContextMenuItem(
    context: IReleaseMenuCommandContext,
    key: string,
    name: string,
    iconProps?: IIconProps,
    onClickFunctionName?: string,
    href?: string): IMenuItem {
    const command: IMenuItem = {
        id: key,
        text: name,
        iconProps: iconProps,
        href: href
    }

    if (onClickFunctionName) {
        command.onActivate = (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
            (context.pageContext.getService<IReleaseCommandsService>(serviceName) as any)[onClickFunctionName](context, event);
        };
    }

    return command;
}

function _getReleaseUrl(pageContext: IVssPageContext, context: IReleaseMenuCommandContext): string {
    return createUrlForRoute(pageContext, "ms.vss-releaseManagement-web.cd-release-progress-default-route", {
        "_a": "release-pipeline-progress",
        "releaseId": context.release.id.toString(10)
    });
}

const viewReleaseFeature: string = "Command_ViewRelease";
const abandonReleaseFeature: string = "Command_AbandonRelease";
const deleteReleaseFeature: string = "Command_DeleteRelease";
const undeleteReleaseFeature: string = "Command_UndeleteRelease";
const changeReleaseRetentionFeature: string = "Command_ChangeRetention";

const serviceName = "IReleaseCommandsService";

Services.add(serviceName, { serviceFactory: ReleaseCommandsService });
